<script lang="ts">
const SKELETON_ROW_COUNT = 3;

const POSITION_CLASS_MAP: Record<'floating' | 'inFlow', string> = {
    floating: 'absolute top-full right-0 left-0 z-20',
    inFlow: 'relative w-full',
};
</script>

<script setup lang="ts">
import { SellingMethod } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { NO_PRICE_PLACEHOLDER } from '~/constants/sale';
import { type PlatformSellingMethod } from '~/types/sale.type';

// PROPS
interface Props {
    items: QuickSearchPreviewItem[];
    total: number;
    sellingMethod: PlatformSellingMethod;
    /** Shown in place of the rows under `isEmpty`; the panel announces the same wording. */
    emptyMessage: string;
    /** The other tab with its count, e.g. "At auction (12)"; empty when that tab has no matches either. */
    otherChannelLabel?: string;
    isPending?: boolean;
    /** The rows answer an older query: dimmed, not hidden, so the list does not flicker while typing. */
    isStale?: boolean;
    isEmpty?: boolean;
    isError?: boolean;
    /** Off in the modal, whose `overflow-hidden` content box cannot grow for an absolute child. */
    isFloating?: boolean;
}

const {
    items,
    total,
    sellingMethod,
    emptyMessage,
    otherChannelLabel = '',
    isPending = false,
    isStale = false,
    isEmpty = false,
    isError = false,
    isFloating = true,
} = defineProps<Props>();

// EVENTS
const emit = defineEmits<{
    /** Same-tab navigation only: a modified click opens a new tab and must leave the search up. */
    navigate: [];
    seeAll: [];
    switchChannel: [];
}>();

// COMPOSABLES
const { tt, tn } = useTypedI18n();

// DATA
const detailRouteName = computed(() => (sellingMethod === SellingMethod.Auction ? ROUTE_NAMES.AUCTION_DETAIL : ROUTE_NAMES.DIRECT_SALE_DETAIL));

// `placeholderData` keeps the previous rows up, so only the first query has nothing to show.
const hasSkeleton = computed<boolean>(() => isPending && !items.length);

// HANDLERS
// Mirrors RouterLink's own guard: it leaves modified clicks to the browser, which opens a new tab or window.
const onItemClick = (event: MouseEvent): void => {
    if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) emit('navigate');
};
</script>

<template>
    <div
        class="bg-default ring-default mt-2 flex max-h-[min(70vh,30rem)] flex-col overflow-hidden rounded-2xl shadow-lg ring-1"
        :class="isFloating ? POSITION_CLASS_MAP.floating : POSITION_CLASS_MAP.inFlow"
        :aria-busy="isStale"
    >
        <ul v-if="hasSkeleton" class="divide-y divide-neutral-300 overflow-y-auto" aria-hidden="true">
            <li v-for="row in SKELETON_ROW_COUNT" :key="row" class="flex items-center gap-3 p-3">
                <USkeleton class="aspect-4/3 h-14 w-auto shrink-0 rounded-lg" />
                <div class="flex grow flex-col gap-2">
                    <USkeleton class="h-3 w-40" />
                    <USkeleton class="h-2 w-24" />
                </div>
                <USkeleton class="h-3 w-24 shrink-0" />
            </li>
        </ul>

        <!-- These two live here, not in the panel: their wording runs long enough to grow the card and reflow the hero. -->
        <p v-else-if="isError" class="text-muted p-4 text-sm" aria-hidden="true">{{ tt('search.quick.preview.error') }}</p>

        <div v-else-if="isEmpty" class="p-4 text-sm">
            <p aria-hidden="true">
                <span class="text-default font-medium">{{ emptyMessage }}</span>
                {{ ' ' }}
                <span class="text-muted">
                    {{ otherChannelLabel ? tt('search.quick.preview.empty.otherChannel') : tt('search.quick.preview.empty.hint') }}
                </span>
            </p>

            <!-- Outside the `aria-hidden` text, since a focusable control must stay in the accessibility tree; its name carries
                 the hidden lead-in, or it would read as a bare "Buy Now (12)" call to action. -->
            <UButton
                v-if="otherChannelLabel"
                variant="link"
                trailing-icon="lucide:arrow-right"
                :label="otherChannelLabel"
                :aria-label="`${tt('search.quick.preview.empty.otherChannel')} ${otherChannelLabel}`"
                class="mt-1 px-0"
                @click="emit('switchChannel')"
            />
        </div>

        <!-- Kept up while stale to avoid flicker, but dimmed: they and the footer count answer the previous query. -->
        <ul
            v-else
            class="divide-y divide-neutral-300 overflow-y-auto transition-opacity"
            :class="{ 'opacity-50': isStale }"
            :aria-label="tt('search.quick.preview.aria.list')"
        >
            <li v-for="item in items" :key="item.id">
                <NuxtLinkLocale
                    :to="{ name: detailRouteName, params: { slug: item.slug } }"
                    class="hover:bg-elevated flex flex-wrap items-center gap-x-3 gap-y-1 p-3 transition-colors"
                    @click="onItemClick"
                >
                    <NuxtImg
                        v-if="item.coverImage"
                        :src="item.coverImage"
                        sizes="240"
                        densities="x1"
                        draggable="false"
                        loading="lazy"
                        class="aspect-4/3 h-14 w-auto shrink-0 rounded-lg object-cover object-center"
                        :alt="`${item.brand} ${item.model}`"
                    />
                    <div v-else class="bg-elevated flex aspect-4/3 h-14 shrink-0 items-center justify-center rounded-lg">
                        <UIcon name="lucide:bus" class="text-dimmed size-5" />
                    </div>

                    <!-- `basis-0` keeps this column beside the thumbnail: a wrapping flex line breaks before it shrinks. -->
                    <div class="flex min-w-0 grow basis-0 flex-col">
                        <span class="text-highlighted truncate text-sm font-medium">{{ item.brand }} {{ item.model }}</span>
                        <span class="text-dimmed truncate text-xs">{{ item.vin }}</span>
                    </div>

                    <span v-if="item.price !== null" class="shrink-0 text-sm font-semibold">{{ tn(item.price, 'currency') }}</span>

                    <!-- Wording needs far more room than a figure, so below `sm` it wraps onto its own line instead of crushing the model name. -->
                    <span
                        v-else-if="sellingMethod === SellingMethod.DirectSale"
                        class="w-full shrink-0 text-right text-xs font-medium sm:w-auto sm:max-w-[45%]"
                    >
                        {{ item.priceOnRequest ? tt('slp.DirectSale.priceOnRequest') : tt('slp.DirectSale.priceForGuests') }}
                    </span>

                    <span v-else class="shrink-0 text-sm font-semibold">{{ NO_PRICE_PLACEHOLDER }}</span>
                </NuxtLinkLocale>
            </li>
        </ul>

        <div
            v-if="!hasSkeleton && !isError && !isEmpty && total > 0"
            class="shrink-0 border-t border-neutral-300 p-2 transition-opacity"
            :class="{ 'opacity-50': isStale }"
        >
            <UButton variant="ghost" block trailing-icon="lucide:arrow-right" :ui="{ base: 'justify-between' }" @click="emit('seeAll')">
                <BaseI18nT keypath="search.quick.preview.seeAll" tag="span" :plural="total">
                    <template #total>{{ total }}</template>
                </BaseI18nT>
            </UButton>
        </div>
    </div>
</template>
